import { CreateLabelText, GetTextStyle } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/menu/AbstractScrollableContentView";
import { BetData } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/menu/paytable/PaytableContent";
import { IOryxServiceProvider } from "@westpierstudio/core-module-oryx/game";
import { PaytableContentFactoryService } from "@westpierstudio/core-module-oryx/services";
import { toNumber } from "lodash";
import { BetConfig, JackpotResets, OpenGameData, OpenGameEvent, RTPVariant } from "servertypes/gameevents/OpenGame";
import { MaxWinConfigsByRTPVariant } from "./MaxWinConfig";
import { SpreadRTPComponent, SpreadRTPEntry, SpreadRTPPayload, SubRTPVariants } from "./SpreadRTPComponent";
import { SpreadRTPDependentComponent, SpreadRTPDependentComponentPayload } from "./SpreadRTPDependentComponent";

export class DragonsVsPandsPaytableContentFactoryService extends PaytableContentFactoryService {

    protected spreadRTP: boolean = true;
    protected showMaxWinInfo: boolean = true;
    protected spreadRTPEntries: SpreadRTPEntry[] = [];
    protected subRTPs: SubRTPVariants = {smallSpin: {betSize1: NaN}, spin: {betSize1: NaN, betSize2: NaN, betSize3: NaN, betSize4: NaN, betSize5: NaN}};
    protected subRTP: number | undefined;

    override createComponent(componentName: string, payload: unknown, services: IOryxServiceProvider, createLabelText: CreateLabelText, getTextStyle: GetTextStyle) {
        switch (componentName) {
            case `spreadRTP`:
                return this.spreadRTP ? this.createSpreadRTPComponent(services, <SpreadRTPPayload>payload, createLabelText) : undefined;
            case `spreadRTPDependent`:
                return this.createSpreadRTPDependent(services, <SpreadRTPDependentComponentPayload>payload, createLabelText);
            default:
                if (componentName === `maxWin` && !this.showMaxWinInfo) { return undefined; }
                return super.createComponent(componentName, payload, services, createLabelText, getTextStyle);
        }
    }

    protected override getCurrentRTP(): number | undefined {
        return this.subRTP;
    }

    protected createSpreadRTPDependent(services: IOryxServiceProvider, payload: SpreadRTPDependentComponentPayload, createLabelText: CreateLabelText) {
        return new SpreadRTPDependentComponent(services, payload, this.spreadRTP, createLabelText, `spreadRTPDependent`);
    }

    protected createSpreadRTPComponent(services: IOryxServiceProvider, payload: SpreadRTPPayload, createLabelText: CreateLabelText) {
        if (this.spreadRTPEntries.length === 0) {
            return undefined;
        }
        return new SpreadRTPComponent(services, payload, this.spreadRTPEntries, createLabelText);
    }

    public setSpreadRTPEntries(entries: SpreadRTPEntry[]) {
        this.spreadRTPEntries = entries;
    }

    public setOpenGameData(event: OpenGameEvent, rtpVariant: RTPVariant) {

        this.spreadRTP = rtpVariant === `spread1`;

        const rtp = event.body.data.rtp;
        if (typeof rtp !== `number`) {
            if (rtp.SMALL_SPIN) this.subRTPs.smallSpin.betSize1 = rtp.SMALL_SPIN[1] * 100;
            if (this.spreadRTP) {
                this.subRTPs.spin.betSize1 = rtp.SPIN[1] * 100;
                this.subRTPs.spin.betSize2 = rtp.SPIN[2] * 100;
                this.subRTPs.spin.betSize3 = rtp.SPIN[3] * 100;
                this.subRTPs.spin.betSize4 = rtp.SPIN[4] * 100;
                this.subRTPs.spin.betSize5 = rtp.SPIN[5] * 100;
            } else {
                const subRTP = rtp.SPIN[1] * 100;
                this.subRTPs.spin.betSize1 = subRTP;
                this.subRTPs.spin.betSize2 = subRTP;
                this.subRTPs.spin.betSize3 = subRTP;
                this.subRTPs.spin.betSize4 = subRTP;
                this.subRTPs.spin.betSize5 = subRTP;
            }
        }

        this.subRTP = this.subRTPs.spin.betSize1;

        this.updateMaxWinConfigs(rtpVariant);
    }

    public updateSubRTPVariant(betData: BetData) {

        if (!this.showMaxWinInfo) { return; }

        if (betData.coinsPerBet === 88) {
            switch (betData.selectedCoin) {
                case 1:
                    this.subRTP = this.subRTPs.spin.betSize1;
                    break;
                case 2:
                    this.subRTP = this.subRTPs.spin.betSize2;
                    break;
                case 3:
                    this.subRTP = this.subRTPs.spin.betSize3;
                    break;
                case 4:
                    this.subRTP = this.subRTPs.spin.betSize4;
                    break;
                default:
                    this.subRTP = this.subRTPs.spin.betSize5;
                    break;
            }
        } else {
            this.subRTP = this.subRTPs.smallSpin.betSize1;
        }
    }

    public updateMaxWinConfigs(rtpVariant: RTPVariant) {
        const rtpConfigs = MaxWinConfigsByRTPVariant[rtpVariant];
        if (!rtpConfigs) {
            console.warn(`No max-win rtp configs found for rtp variant ${rtpVariant}`);
            return;
        }

        this.setMaxWinConfigs(rtpConfigs);
    }

    public updateJackpotPayouts(services: IOryxServiceProvider, openGame: OpenGameData, selectedCoin: number, coinsPerBet: number) {

        const uncappedMultiplier = selectedCoin;

        if (uncappedMultiplier === 1 && coinsPerBet === 48) {
            // no jackpots in small-spin so just display "-" as a value and early-exit
            this.setToken(`JACKPOT_VALUE_P1`, `-`);
            this.setToken(`JACKPOT_VALUE_P2`, `-`);
            this.setToken(`JACKPOT_VALUE_P3`, `-`);
            this.setToken(`JACKPOT_VALUE_P4`, `-`);
            this.setToken(`JACKPOT_VALUE_P5`, `-`);
            this.setToken(`JACKPOT_VALUE_P6`, `-`);
            return;
        }

        const jackpotResets = openGame.jackpotResets;
        const jackpotResetMultipliers = Object.keys(jackpotResets).map(toNumber);

        const maxJackpotResetMultiplier = Math.max(...jackpotResetMultipliers);
        const isCapped = (uncappedMultiplier >= maxJackpotResetMultiplier);
        const cappedMultiplier = isCapped ? maxJackpotResetMultiplier : uncappedMultiplier;

        const format = (value: number) => services.currency.formatCoins(value, {group: true});

        const getValue = (jackpot: keyof JackpotResets) => jackpotResets[cappedMultiplier][jackpot];

        // P1 payout below the cap does not get the additional multiplier scaling, instead it hits more frequently as the multiplier goes up.
        this.setToken(`JACKPOT_VALUE_P1`, isCapped ? format(uncappedMultiplier * getValue(`P1`)) : format(getValue(`P1`)));
        this.setToken(`JACKPOT_VALUE_P2`, format(uncappedMultiplier * getValue(`P2`)));
        this.setToken(`JACKPOT_VALUE_P3`, format(uncappedMultiplier * getValue(`P3`)));
        this.setToken(`JACKPOT_VALUE_P4`, format(uncappedMultiplier * getValue(`P4`)));
        this.setToken(`JACKPOT_VALUE_P5`, format(uncappedMultiplier * getValue(`P5`)));
        this.setToken(`JACKPOT_VALUE_P6`, format(uncappedMultiplier * getValue(`P6`)));
    }
}


export function toSpreadRTPEntry(coinSize: number, rtpFraction: number, betConfig: BetConfig): SpreadRTPEntry {
    return {
        betAmount: coinSize * betConfig.coinsPerBet,
        rtp: rtpFraction * 100
    };
};

export function toSpreadRTPEntries(data: Record<number, number>, betConfig: BetConfig): SpreadRTPEntry[] {
    return Object.entries(data).map(([coinSize, rtpFraction]) => toSpreadRTPEntry(+coinSize, rtpFraction, betConfig));
};